// Token Management Utility
// Handles secure storage and automatic refresh of JWT tokens

interface TokenData {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // Timestamp when access token expires
}

class TokenManager {
  private static instance: TokenManager;
  private tokenData: TokenData | null = null;
  private refreshPromise: Promise<TokenData> | null = null;

  private constructor() {
    // Try to restore tokens from localStorage on initialization
    this.loadTokens();
  }

  // Safe localStorage methods that handle errors gracefully
  private safeGetItem(key: string): string | null {
    try {
      if (typeof localStorage !== 'undefined' && localStorage) {
        return localStorage.getItem(key);
      }
    } catch (e) {
      // Silently fail - in incognito mode or other restricted environments
    }
    return null;
  }

  private safeSetItem(key: string, value: string): void {
    try {
      if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      // Silently fail - in incognito mode or other restricted environments
      console.warn('Failed to store data in localStorage (incognito mode?)');
    }
  }

  private safeRemoveItem(key: string): void {
    try {
      if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.removeItem(key);
      }
    } catch (e) {
      // Silently fail - in incognito mode or other restricted environments
    }
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  // Store tokens securely
  setTokens(accessToken: string, refreshToken: string): void {
    console.log('[TokenManager] setTokens called with:', {
      accessTokenLength: accessToken?.length,
      refreshTokenLength: refreshToken?.length,
      accessTokenStart: accessToken?.substring(0, 20) + '...',
      refreshTokenStart: refreshToken?.substring(0, 20) + '...'
    });
    
    // Parse JWT to get expiration time
    const payload = this.parseJWT(accessToken);
    
    // If token parsing failed or doesn't have our expected fields, reject it
    if (!payload) {
      console.error('[TokenManager] Invalid token format, refusing to store. Token:', accessToken);
      // Don't throw error, just return to avoid breaking the app
      return;
    }
    
    console.log('[TokenManager] Storing tokens, payload:', payload);
    
    const expiresAt = payload?.exp ? payload.exp * 1000 : Date.now() + 30 * 60 * 1000; // Default 30 min

    this.tokenData = {
      accessToken,
      refreshToken,
      expiresAt,
    };

    // Store in localStorage for persistence
    // Note: In production, consider using more secure storage methods
    this.safeSetItem('ev_charging_auth_tokens_v2', JSON.stringify(this.tokenData));
  }

  // Get current access token, refreshing if needed
  async getAccessToken(): Promise<string | null> {
    if (!this.tokenData) {
      return null;
    }

    // Check if token is about to expire (5 minutes buffer)
    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes
    
    if (this.tokenData.expiresAt - now < bufferTime) {
      // Token is expired or about to expire, refresh it
      try {
        await this.refreshAccessToken();
      } catch (error) {
        // Refresh failed, clear tokens
        this.clearTokens();
        throw error;
      }
    }

    return this.tokenData?.accessToken || null;
  }

  // Get refresh token
  getRefreshToken(): string | null {
    return this.tokenData?.refreshToken || null;
  }

  // Refresh access token using refresh token
  async refreshAccessToken(): Promise<TokenData> {
    console.log('[TokenManager] refreshAccessToken called:', {
      hasRefreshPromise: !!this.refreshPromise,
      hasTokenData: !!this.tokenData,
      hasRefreshToken: !!this.tokenData?.refreshToken
    });

    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      console.log('[TokenManager] Refresh already in progress, waiting for existing refresh...');
      return this.refreshPromise;
    }

    if (!this.tokenData?.refreshToken) {
      console.error('[TokenManager] No refresh token available for refresh');
      throw new Error('No refresh token available');
    }

    console.log('[TokenManager] Starting new token refresh...');
    this.refreshPromise = this.performRefresh();

    try {
      const result = await this.refreshPromise;
      console.log('[TokenManager] Token refresh completed successfully');
      return result;
    } catch (error) {
      console.error('[TokenManager] Token refresh failed:', error);
      throw error;
    } finally {
      this.refreshPromise = null;
      console.log('[TokenManager] Refresh promise cleared');
    }
  }

  private async performRefresh(): Promise<TokenData> {
    const { API_CONFIG } = await import('../config/api');

    const oldRefreshToken = this.tokenData?.refreshToken?.substring(0, 20) + '...';
    console.log('[TokenManager] performRefresh: Calling backend refresh API', {
      oldRefreshTokenPreview: oldRefreshToken,
      endpoint: `${API_CONFIG.baseURL}${API_CONFIG.endpoints.auth.refresh}`
    });

    const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.auth.refresh}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken: this.tokenData!.refreshToken,
      }),
    });

    if (!response.ok) {
      console.error('[TokenManager] Refresh API failed:', {
        status: response.status,
        statusText: response.statusText
      });
      const errorText = await response.text();
      console.error('[TokenManager] Error response body:', errorText);
      throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[TokenManager] Refresh API successful:', {
      hasAccessToken: !!data.accessToken,
      hasRefreshToken: !!data.refreshToken,
      newRefreshTokenPreview: data.refreshToken?.substring(0, 20) + '...',
      responseKeys: Object.keys(data)
    });

    try {
      console.log('[TokenManager] Setting new tokens from refresh response...');
      this.setTokens(data.accessToken, data.refreshToken);
      console.log('[TokenManager] New tokens set successfully');
    } catch (error) {
      console.error('[TokenManager] Failed to set tokens after refresh:', error);
      throw error;
    }

    return this.tokenData!;
  }

  // Clear all tokens (logout)
  clearTokens(): void {
    console.log('[TokenManager] clearTokens called:', {
      hadTokenData: !!this.tokenData,
      hadRefreshToken: !!this.tokenData?.refreshToken,
      stackTrace: new Error().stack?.split('\n').slice(0, 5).join('\n')
    });
    this.tokenData = null;
    // Remove both old and new keys to ensure clean logout
    this.safeRemoveItem('ev_auth_tokens');
    this.safeRemoveItem('ev_charging_auth_tokens_v2');
    console.log('[TokenManager] All tokens cleared from memory and localStorage');
  }

  // Load tokens from storage
  private loadTokens(): void {
    // Try new key first
    let stored = this.safeGetItem('ev_charging_auth_tokens_v2');

    // If not found, try old key and migrate
    if (!stored) {
      stored = this.safeGetItem('ev_auth_tokens');
      if (stored) {
        // Migrate to new key
        this.safeSetItem('ev_charging_auth_tokens_v2', stored);
        this.safeRemoveItem('ev_auth_tokens');
      }
    }

    if (stored) {
      try {
        const data = JSON.parse(stored) as TokenData;

        // IMPORTANT: Always load the token data if we have a refresh token
        // Even if the access token is expired, the refresh token might still be valid
        // The apiClient will handle refreshing the access token when needed
        if (data.refreshToken) {
          this.tokenData = data;
          console.log('[TokenManager] Loaded tokens from storage:', {
            hasAccessToken: !!data.accessToken,
            hasRefreshToken: !!data.refreshToken,
            accessTokenExpired: data.expiresAt <= Date.now(),
            expiresAt: new Date(data.expiresAt).toISOString()
          });
        } else {
          // No refresh token means we can't recover - clear everything
          console.log('[TokenManager] No refresh token in stored data, clearing');
          this.safeRemoveItem('ev_charging_auth_tokens_v2');
          this.safeRemoveItem('ev_auth_tokens');
        }
      } catch (e) {
        console.error('Failed to parse stored tokens:', e);
        this.safeRemoveItem('ev_charging_auth_tokens_v2');
        this.safeRemoveItem('ev_auth_tokens');
      }
    }
  }

  // Parse JWT token payload
  private parseJWT(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT format');
        return null;
      }
      
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      
      // Validate that this is our token by checking for expected fields
      // Our tokens should have sub (user ID) 
      if (!payload.sub) {
        console.warn('JWT missing user ID (sub), might be from external app');
        return null;
      }
      
      // Note: Our backend doesn't set an issuer, so we don't check for it
      // Just validate that we have the required fields
      
      return payload;
    } catch (e) {
      console.error('Failed to parse JWT:', e);
      return null;
    }
  }

  // Check if user is authenticated (has tokens, not necessarily valid)
  // This should return true as long as we have a refresh token,
  // even if the access token is expired (we can refresh it)
  isAuthenticated(): boolean {
    return !!this.tokenData && !!this.tokenData.refreshToken;
  }

  // Check if the current access token is still valid
  // Separate from isAuthenticated - this checks token expiry
  isTokenValid(): boolean {
    const isValid = !!this.tokenData && this.tokenData.expiresAt > Date.now();
    console.log('[TokenManager] isTokenValid check:', {
      hasTokenData: !!this.tokenData,
      expiresAt: this.tokenData?.expiresAt,
      now: Date.now(),
      isValid
    });
    return isValid;
  }

  // Check if we're currently refreshing the token
  isRefreshing(): boolean {
    return !!this.refreshPromise;
  }
}

export const tokenManager = TokenManager.getInstance();